import { useEffect } from 'react';
import { PushNotifications, Token, PushNotificationSchema } from '@capacitor/push-notifications';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useToast } from './use-toast';
import { Capacitor } from '@capacitor/core';

export function usePushNotifications() {
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    // Only initialize on native platforms
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    initializePushNotifications();
  }, []);

  const initializePushNotifications = async () => {
    try {
      // Request permission
      const permission = await PushNotifications.requestPermissions();
      
      if (permission.receive === 'granted') {
        // Register with FCM
        await PushNotifications.register();
      }

      // Listen for registration success
      PushNotifications.addListener('registration', async (token: Token) => {
        console.log('Push registration success, token:', token.value);
        await saveDeviceToken(token.value);
      });

      // Listen for registration errors
      PushNotifications.addListener('registrationError', (error: any) => {
        console.error('Error on registration:', error);
      });

      // Listen for notifications received while app is in foreground
      PushNotifications.addListener(
        'pushNotificationReceived',
        (notification: PushNotificationSchema) => {
          console.log('Push notification received:', notification);
          
          toast({
            title: notification.title || 'Notification',
            description: notification.body || '',
          });
        }
      );

      // Listen for notification actions (when user taps notification)
      PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (notification: any) => {
          console.log('Push notification action performed:', notification);
          
          const data = notification.notification.data;
          
          // Handle different notification types
          if (data.type === 'new_order' && data.orderId) {
            navigate(`/vendor-dashboard?order=${data.orderId}`);
          } else if (data.type === 'order_ready' && data.orderId) {
            navigate(`/orders/${data.orderId}`);
          } else if (data.type === 'order_status' && data.orderId) {
            navigate(`/orders/${data.orderId}`);
          } else if (data.type === 'vendor_message' && data.conversationId) {
            navigate(`/messages/${data.conversationId}`);
          } else if (data.productId) {
            navigate(`/product/${data.productId}`);
          }
        }
      );

    } catch (error) {
      console.error('Error initializing push notifications:', error);
    }
  };

  const saveDeviceToken = async (token: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.log('No user logged in, token will be saved after login');
        // Store token locally to save after login
        localStorage.setItem('pending_push_token', token);
        return;
      }

      // Check if token already exists
      const { data: existing } = await supabase
        .from('device_tokens')
        .select('id')
        .eq('token', token)
        .eq('user_id', user.id)
        .single();

      if (existing) {
        // Update last_used_at
        await supabase
          .from('device_tokens')
          .update({ last_used_at: new Date().toISOString() })
          .eq('id', existing.id);
      } else {
        // Insert new token
        await supabase
          .from('device_tokens')
          .insert({
            user_id: user.id,
            token: token,
            platform: 'android',
            device_info: {
              userAgent: navigator.userAgent,
              platform: Capacitor.getPlatform(),
            }
          });
      }

      console.log('Device token saved successfully');
    } catch (error) {
      console.error('Error saving device token:', error);
    }
  };

  const checkPendingToken = async () => {
    const pendingToken = localStorage.getItem('pending_push_token');
    if (pendingToken) {
      await saveDeviceToken(pendingToken);
      localStorage.removeItem('pending_push_token');
    }
  };

  return {
    checkPendingToken
  };
}
